import React from 'react';
import {Popup} from 'semantic-ui-react';
import {uniqWith as _uniqWith, isEqual as _isEqual, get as _get} from 'lodash';

import {convertToInt} from '@utils/helpers';
import {
    clientPackageName,
    geoFields,
    productShortName,
} from '@modules/booking/utils/clientBookingsFields';
import {
    COMPETITOR_NOT_DEFINED,
    COMPETITOR_NOT_DEFINED_BOTH,
    EVENT_NON_SPORTRADAR_TYPE,
    EVENT_SPORTRADAR_TYPE,
    EVENT_STAGE_TYPE,
} from '@constants/variables';
import {columnObjectAlignCenter, columnObjectAlignLeft} from '@utils/tableHelpers';
import {CLIENT_BOOKINGS_TABLE_INVOICE_STATUS_TEXT} from '@modules/client/constants/messages';

export const CATEGORY_FIELD_MAP = {
    default: {
        1: 'sport',
        2: 'tournament_category',
        3: 'tournament',
    },
    stageBased: {
        1: 'sport',
        2: '1st_category',
        3: '2nd_category',
    },
};

export const CATEGORY_LEVEL_CONFIG = {
    1: {
        key: 'content_category_level_1_name',
        label: 'Sport /',
        defaultSubLabel: '1st level category',
    },
    2: {
        key: 'content_category_level_2_name',
        label: 'Category /',
        defaultSubLabel: '2nd level category',
        booking: {
            subLabel: '1st stage category /',
            subSubLabel: '2nd level category',
        },
    },
    3: {
        key: 'content_category_level_3_name',
        label: 'Tournament /',
        defaultSubLabel: '3rd level category',
        booking: {
            subLabel: '2nd stage category /',
            subSubLabel: '3rd level category',
        },
    },
};

/**
 * Render data for Invoice Status column
 *
 * @param {Object} row
 *
 * @returns {string|columns.event_invoice_status_name|{sorting, header, label, content}|*}
 */
export const renderInvoiceStatusColumn = (row) => {
    if (row.booking_invoice_status_name && row.product_is_invoiceable) {
        return (
            <Popup
                trigger={
                    <b data-testid='popup-trigger'>
                        <i>{row.booking_invoice_status_name}</i>
                    </b>
                }
                content={
                    <span data-testid='popup-content'>{CLIENT_BOOKINGS_TABLE_INVOICE_STATUS_TEXT}</span>
                }
            />
        );
    } else if (row.event_invoice_status_name && row.product_is_invoiceable) {
        return row.event_invoice_status_name;
    } else {
        return '';
    }
};

export const getInvoiceStatus = (row) => {
    if (row.booking_invoice_status_name && row.product_is_invoiceable) {
        return row.booking_invoice_status_name;
    } else if (row.event_invoice_status_name && row.product_is_invoiceable) {
        return row.event_invoice_status_name;
    } else {
        return ' ';
    }
};

export const getCategoryMappingKey = (row) => {
    if (row.event_type === EVENT_STAGE_TYPE) {
        return 'stageBased';
    }

    return 'default';
};

export const renderCategoryLevel = (row, level) => {
    const mappingKey = getCategoryMappingKey(row);
    const field = CATEGORY_FIELD_MAP[mappingKey][level];

    return renderContentCategoryLevelColumn(row, field, level);
};

export const renderContentCategoryLevelColumn = (row, field, level) => {
    if (row.event_type === EVENT_STAGE_TYPE) {
        return row[`stage_${field}_name`];
    }

    if (row.event_type === EVENT_SPORTRADAR_TYPE) {
        const isMatch = row.event_booking_id ? Boolean(row.match_id) : Boolean(row.match_or_stage_id);
        const prefix = isMatch ? 'match' : 'sr_event';

        return row[`${prefix}_${field}_name`];
    }

    return row[`content_category_level_${level}_name`];
};

export const getContentTierName = (row) =>
    row.av_event_content_tier_name ??
    row.sr_event_content_tier_name ??
    row.match_content_tier_name ??
    row.st_event_content_tier_name ??
    null;
export const getCompetitors = (row) => {
    let competitors = [],
        defaultCompetitors = COMPETITOR_NOT_DEFINED_BOTH;

    if (row.match_id) {
        competitors = getCompetitorsByMatch(row);
    } else if (
        row.event_type === EVENT_SPORTRADAR_TYPE &&
        (null !== row.sr_event_home_competitor || null !== row.sr_event_away_competitor)
    ) {
        competitors.push(getCompetitorsByField(row, 'sr_event_home_competitor'));
        competitors.push(getCompetitorsByField(row, 'sr_event_away_competitor'));
    } else if (
        row.event_type === EVENT_NON_SPORTRADAR_TYPE &&
        (null !== row.av_event_home_competitor || null !== row.av_event_away_competitor)
    ) {
        competitors.push(getCompetitorsByField(row, 'av_event_home_competitor'));
        competitors.push(getCompetitorsByField(row, 'av_event_away_competitor'));
    }

    if (1 === _uniqWith(competitors, _isEqual).length && COMPETITOR_NOT_DEFINED === competitors[0]) {
        competitors = [];
    }

    return 0 < competitors.length ? competitors.join(' : ') : defaultCompetitors;
};

const getCompetitorsByMatch = (data) => {
    const competitors = [];

    competitors.push(getCompetitorsByField(data, 'match_home_competitor'));
    competitors.push(getCompetitorsByField(data, 'match_away_competitor'));

    return competitors;
};

const getCompetitorsByField = (data, competitorField) => {
    if (competitorField && null !== data[competitorField]) {
        return data[competitorField];
    } else {
        return COMPETITOR_NOT_DEFINED;
    }
};

export const showExternalDescription = (label, externalDescription = null) => {
    if (!externalDescription) return label;

    return (
        <Popup
            trigger={<span data-testid='popup-trigger'>{label}</span>}
            content={
                <span data-testid='popup-content'>
                    <strong>External description: </strong> {externalDescription}
                </span>
            }
            size='small'
            hoverable
        />
    );
};

export const getTypeOverrides = (type, config) => {
    if (!type || !config) return {};

    return config[type] || {};
};

export const getContentCategoryLevelColumn = (level, type = '') => {
    const config = CATEGORY_LEVEL_CONFIG[level];

    if (!config) return {};

    const typeOverrides = getTypeOverrides(type, config);

    return {
        [config.key]: {
            label: config.label,
            sorting: (row) => renderCategoryLevel(row, level),
            subLabel:
                typeOverrides.subLabel || config.defaultSubLabel,
            ...(typeOverrides.subSubLabel && {subSubLabel: typeOverrides.subSubLabel}),
            ...columnObjectAlignLeft,
        },
    };
};

export const getBasicClientBookingsColumnsForBookingsTable = (type) => {
    return {
        [`${type}_id`]: {
            label: 'ID',
            sorting: (row) => convertToInt(row[`${type}_id`]),
            header: {
                align: 'center',
                isCollapsed: true,
            },
            content: {
                align: 'center',
            },
        },
        'event_id': {
            label: 'Event ID',
            sorting: (row) => convertToInt(row.event_id),
            ...columnObjectAlignCenter,
        },
        'match_or_stage_id': {
            label: 'Match ID /',
            ...(('booking' === type || 'offering' === type) && {subLabel: 'Stage ID'}),
            sorting: (row) => row.match_or_stage_id ? convertToInt(row.match_or_stage_id) : 0,
            ...columnObjectAlignCenter,
        },
        'event_content_id': {
            label: 'Content ID',
            sorting: (row) => convertToInt(row.event_content_id),
            header: {
                align: 'center',
            },
            content: {
                align: 'center',
            },
        },
        'distribution_id': {
            label: 'Distribution ID',
            sorting: (row) => convertToInt(row.distribution_id),
            header: {
                align: 'center',
            },
            content: {
                align: 'center',
            },
        },
        'start_datetime': {
            label: 'Date/Time',
            sorting: row => _get(row, 'start_datetime', null),
            header: {
                align: 'center',
            },
            content: {
                align: 'center',
                singleLine: true,
            },
        },
        ...getContentCategoryLevelColumn(1, 'booking'),
        ...getContentCategoryLevelColumn(2, 'booking'),
        ...getContentCategoryLevelColumn(3, 'booking'),
        'content_tier_name': {
            label: 'Tier',
            sorting: true,
            ...columnObjectAlignLeft,
        },
        'competitors': {
            label: 'Competitors / Name / Description',
            sorting: row => row.is_stage_event ? row.stage_name_or_description : row.competitors,
            header: {
                align: 'left',
            },
            content: {
                align: 'left',
            },
        },
        'event_content_type_name': {
            label: 'Content',
            sorting: (row) => (`${row.event_content_type_name}${row.event_content_name ? ' - ' + row.event_content_name : ''}`),
            header: {
                align: 'left',
            },
            content: {
                align: 'left',
            },
        },
        ...productShortName,
        'distribution_type_name': {
            label: 'Distribution type',
            sorting: true,
            header: {
                align: 'left',
            },
            content: {
                align: 'left',
            },
        },
        ...geoFields,
        'blackoutZones': {label: 'Blackout Zones', sorting: false, className: 'noPadding'},
        ...clientPackageName,
        'is_hq': {
            label: 'HQ',
            sorting: true,
            ...columnObjectAlignCenter,
        },
        'is_ll': {
            label: 'LL',
            sorting: true,
            ...columnObjectAlignCenter,
        },
    };
};
